import React, { useEffect, useMemo, useState } from 'react';
import {
    CREATE,
    GET_LIST,
    GET_ONE,
    UPDATE,
    authHeader,
    notify,
    provider,
} from '../../_utils';
import {
    cloneMotivationConfig,
    DEFAULT_MOTIVATION_CONFIG,
    emptyHourTier,
    emptyOccTier,
} from '../motivation/defaults';
import { calcPayroll, formatHours, formatMoney, motivationLabel } from '../motivation/formula';

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const Field = ({ label, children, span }) => (
    <label className={`pc-emp-fl${span ? ` pc-emp-fl--${span}` : ''}`}>
        <span>{label}</span>
        {children}
    </label>
);

const num = (value) => {
    if (value === '' || value == null) return '';
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : '';
};

const isOk = (status) => status >= 200 && status < 300;

const MotivationModal = ({ open, onClose }) => {
    const [versions, setVersions] = useState([]);
    const [currentId, setCurrentId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState({
        name: 'Схема мотивации',
        comment: '',
        config: cloneMotivationConfig(),
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [previewHours, setPreviewHours] = useState(140);
    const [previewOcc, setPreviewOcc] = useState(52);
    const [previewKind, setPreviewKind] = useState('Contractor');
    const [previewNorm, setPreviewNorm] = useState(168);
    const [previewParttime, setPreviewParttime] = useState('Full');

    const selected = useMemo(
        () => versions.find((item) => (item._id || item.id) === selectedId) || null,
        [versions, selectedId]
    );
    const isDraft = !selected || selected.status === 'draft';
    const used = Boolean(selected?.usedInCalculations);

    const preview = useMemo(() => calcPayroll({
        totalTime: Number(previewHours) * 3600,
        paidTime: Number(previewHours) * 3600 * 0.85,
        occPercent: previewOcc,
        contractType: previewKind,
        parttime: previewParttime,
        normFullHours: Number(previewNorm) || 0,
        allowance: 0,
    }, form.config), [form.config, previewHours, previewOcc, previewKind, previewParttime, previewNorm]);

    const load = async () => {
        setLoading(true);
        try {
            const list = await provider(GET_LIST, '/operators/motivation', {
                filter: {},
                sort: { field: 'version', order: 'DESC' },
                pagination: { page: 1, perPage: 200 },
            });
            const rows = isOk(list.status) ? (list.data || []) : [];
            setVersions(rows);
            const current = await provider(GET_ONE, '/operators/motivation', 'current');
            const currentRow = isOk(current.status) ? current.data : null;
            const cid = currentRow?._id || currentRow?.id || rows.find((row) => row.status === 'active')?._id;
            setCurrentId(cid || null);
            const first = cid || (rows[0] && (rows[0]._id || rows[0].id));
            if (first) {
                setSelectedId(first);
                const match = rows.find((row) => (row._id || row.id) === first) || currentRow;
                if (match) {
                    setForm({
                        name: match.name || 'Схема мотивации',
                        comment: match.comment || '',
                        config: cloneMotivationConfig(match.config || DEFAULT_MOTIVATION_CONFIG),
                    });
                }
            } else {
                setForm({
                    name: 'Схема из файла расчета ЗП',
                    comment: 'Стартовая схема: часы × 0.98, тарифные пороги 100/135/165, премия по OCC, оклад полной ставки 1600, 8 ч в рабочем дне.',
                    config: cloneMotivationConfig(),
                });
            }
            if (!isOk(list.status)) {
                notify({
                    message: list.message || 'Версии мотивации пока недоступны. Можно подготовить схему и сохранить, когда API будет готов.',
                    type: 'warning',
                });
            }
        } catch (error) {
            notify({ message: 'Не удалось загрузить версии мотивации', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const setConfig = (patch) => setForm((prev) => ({ ...prev, config: { ...prev.config, ...patch } }));

    const updateTier = (key, index, patch) => {
        const next = [...(form.config[key] || [])];
        next[index] = { ...next[index], ...patch };
        setConfig({ [key]: next });
    };

    const removeTier = (key, index) => {
        setConfig({ [key]: (form.config[key] || []).filter((_, i) => i !== index) });
    };

    const selectVersion = (row) => {
        const id = row._id || row.id;
        setSelectedId(id);
        setForm({
            name: row.name || 'Схема мотивации',
            comment: row.comment || '',
            config: cloneMotivationConfig(row.config || DEFAULT_MOTIVATION_CONFIG),
        });
    };

    const payload = () => {
        const { contractNormHours, ...config } = form.config || {};
        return {
            name: form.name,
            comment: form.comment,
            config,
        };
    };

    const saveDraft = async () => {
        setSaving(true);
        try {
            if (selected && isDraft && !used) {
                const { status, message } = await provider(UPDATE, '/operators/motivation', {
                    id: selected._id || selected.id,
                    data: payload(),
                });
                if (isOk(status)) {
                    notify({ message: 'Черновик сохранён', type: 'success' });
                    await load();
                } else {
                    notify({ message: message || 'Не удалось сохранить черновик', type: 'error' });
                }
            } else {
                await saveNew();
            }
        } finally {
            setSaving(false);
        }
    };

    const saveNew = async () => {
        setSaving(true);
        try {
            const { status, data, message } = await provider(CREATE, '/operators/motivation', { data: payload() });
            if (isOk(status)) {
                notify({ message: 'Создана новая версия мотивации', type: 'success' });
                const id = data?._id || data?.id;
                await load();
                if (id) setSelectedId(id);
            } else {
                notify({ message: message || 'Не удалось создать версию', type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const activate = async () => {
        const id = selected?._id || selected?.id;
        if (!id) return;
        setSaving(true);
        try {
            const response = await fetch(`/api/operators/motivation/${id}/activate`, {
                credentials: 'same-origin',
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                notify({ message: 'Версия активирована', type: 'success' });
                await load();
            } else {
                notify({ message: 'Не удалось активировать версию', type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const newFromCurrent = () => {
        setSelectedId(null);
        setForm((prev) => ({
            name: prev.name || 'Схема мотивации',
            comment: prev.comment || '',
            config: cloneMotivationConfig(prev.config),
        }));
    };

    return (
        <div className="pc-emp-ov" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="pc-emp-modal pc-emp-modal--xl" role="dialog" aria-modal="true" aria-labelledby="pc-mot-title">
                <div className="pc-emp-mh">
                    <div>
                        <h3 id="pc-mot-title">Система мотивации</h3>
                        <div className="sub">Версии схемы расчёта ЗП. Исторические расчёты всегда ссылаются на версию, по которой они были сделаны.</div>
                    </div>
                    <button type="button" className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet" onClick={onClose} title="Закрыть">
                        <Icon d="M4 4l8 8M12 4l-8 8" />
                    </button>
                </div>
                <div className="pc-mot">
                    <aside className="pc-mot-list">
                        <div className="pc-mot-list__h">
                            <span>Версии</span>
                            <button type="button" className="pc-emp-btn pc-emp-btn--sm" onClick={newFromCurrent}>Новая</button>
                        </div>
                        {loading ? (
                            <div className="pc-emp-empty"><p>Загрузка…</p></div>
                        ) : (
                            <button
                                type="button"
                                className={`pc-mot-item${!selectedId ? ' on' : ''}`}
                                onClick={newFromCurrent}
                            >
                                <b>Черновик</b>
                                <span>ещё не сохранена</span>
                            </button>
                        )}
                        {versions.map((row) => {
                            const id = row._id || row.id;
                            const active = id === currentId;
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    className={`pc-mot-item${id === selectedId ? ' on' : ''}`}
                                    onClick={() => selectVersion(row)}
                                >
                                    <b>{motivationLabel(row)}</b>
                                    <span>
                                        {row.status === 'active' || active ? 'активна' : (row.status || 'черновик')}
                                        {row.createdAt ? ` · ${new Date(row.createdAt).toLocaleDateString('ru-RU')}` : ''}
                                    </span>
                                </button>
                            );
                        })}
                    </aside>
                    <div className="pc-emp-mb pc-mot-body">
                        <div className="pc-emp-sect">
                            <div className="pc-emp-sect__t">Карточка версии</div>
                            <div className="pc-emp-fg">
                                <Field label="Название" span={2}>
                                    <input className="pc-emp-inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                </Field>
                                <Field label="Комментарий" span={2}>
                                    <textarea className="pc-emp-inp pc-emp-area" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
                                </Field>
                            </div>
                            {selected && used ? (
                                <div className="pc-emp-note">Эта версия уже использовалась в расчётах. Изменения сохраняются только как новая версия.</div>
                            ) : null}
                        </div>

                        <div className="pc-emp-sect">
                            <div className="pc-emp-sect__t">Базовые параметры</div>
                            <div className="pc-emp-fg pc-emp-fg--3">
                                <Field label="Коэффициент перерасчёта часов">
                                    <input
                                        className="pc-emp-inp pc-emp-inp--mono"
                                        type="number"
                                        step="0.01"
                                        value={form.config.hoursCoefficient}
                                        onChange={(e) => setConfig({ hoursCoefficient: num(e.target.value) })}
                                    />
                                </Field>
                                <Field label="Оклад полной ставки, р">
                                    <input
                                        className="pc-emp-inp pc-emp-inp--mono"
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={form.config.contractMonthlySalary}
                                        onChange={(e) => setConfig({ contractMonthlySalary: num(e.target.value) })}
                                    />
                                </Field>
                                <Field label="Часов в рабочем дне">
                                    <input
                                        className="pc-emp-inp pc-emp-inp--mono"
                                        type="number"
                                        step="0.5"
                                        min="0.5"
                                        max="24"
                                        value={form.config.hoursPerDay}
                                        onChange={(e) => setConfig({ hoursPerDay: num(e.target.value) })}
                                    />
                                </Field>
                                <Field label="Премия по умолчанию, %">
                                    <input
                                        className="pc-emp-inp pc-emp-inp--mono"
                                        type="number"
                                        step="1"
                                        value={form.config.defaultBonusPercent}
                                        onChange={(e) => setConfig({ defaultBonusPercent: num(e.target.value) })}
                                    />
                                </Field>
                            </div>
                            <p className="pc-mot-hint">
                                Перерасчёт часов = общее время × коэффициент (в Excel: общее время × 0.98).
                                Норма месяца считается по производственному календарю при расчёте ЗП: рабочие дни × часов в рабочем дне.
                                Для контракта к выплате: оклад ставки / личная норма × оплачиваемые часы. Подряд от календарной нормы не зависит.
                            </p>
                        </div>

                        <div className="pc-emp-sect">
                            <div className="pc-emp-sect__t">
                                Тариф по часам
                                <button type="button" onClick={() => setConfig({ hourTiers: [...form.config.hourTiers, emptyHourTier()] })}>Добавить порог</button>
                            </div>
                            <div className="pc-mot-table">
                                <div className="pc-mot-tr pc-mot-tr--h">
                                    <span>До часов</span>
                                    <span>Ставка, р/ч</span>
                                    <span>Режим</span>
                                    <span />
                                </div>
                                {(form.config.hourTiers || []).map((tier, index) => {
                                    const unbounded = tier.mode === 'overflow' || tier.maxHours == null;
                                    return (
                                    <div key={`h-${index}`}>
                                        <div className="pc-mot-tr">
                                            {unbounded ? (
                                                <span className="pc-mot-open">и выше</span>
                                            ) : (
                                                <input
                                                    className="pc-emp-inp pc-emp-inp--mono"
                                                    type="number"
                                                    min="0"
                                                    placeholder="100"
                                                    value={tier.maxHours}
                                                    onChange={(e) => updateTier('hourTiers', index, { maxHours: e.target.value === '' ? 100 : num(e.target.value) })}
                                                />
                                            )}
                                            <input
                                                className="pc-emp-inp pc-emp-inp--mono"
                                                type="number"
                                                step="0.1"
                                                value={tier.rate}
                                                onChange={(e) => updateTier('hourTiers', index, { rate: num(e.target.value) })}
                                            />
                                            <select
                                                className="pc-emp-inp"
                                                value={tier.mode || 'all'}
                                                onChange={(e) => updateTier('hourTiers', index, {
                                                    mode: e.target.value,
                                                    maxHours: e.target.value === 'overflow' ? null : (tier.maxHours == null ? 100 : tier.maxHours),
                                                    baseHours: e.target.value === 'overflow' ? (tier.baseHours || 165) : undefined,
                                                    baseRate: e.target.value === 'overflow' ? (tier.baseRate || 6) : undefined,
                                                })}
                                            >
                                                <option value="all">Все часы по ставке</option>
                                                <option value="overflow">Сверх нормы: база + остаток</option>
                                            </select>
                                            <button type="button" className="pc-emp-btn pc-emp-btn--sm" onClick={() => removeTier('hourTiers', index)}>Удалить</button>
                                        </div>
                                        {tier.mode === 'overflow' ? (
                                            <div className="pc-mot-tr" style={{ marginTop: 6 }}>
                                                <input
                                                    className="pc-emp-inp pc-emp-inp--mono"
                                                    type="number"
                                                    title="Часы базы"
                                                    value={tier.baseHours || ''}
                                                    onChange={(e) => updateTier('hourTiers', index, { baseHours: num(e.target.value) })}
                                                />
                                                <input
                                                    className="pc-emp-inp pc-emp-inp--mono"
                                                    type="number"
                                                    step="0.1"
                                                    title="Ставка базы"
                                                    value={tier.baseRate || ''}
                                                    onChange={(e) => updateTier('hourTiers', index, { baseRate: num(e.target.value) })}
                                                />
                                                <span className="pc-mot-hint" style={{ margin: 0 }}>База часов × ставка базы, остаток × ставка порога</span>
                                            </div>
                                        ) : null}
                                    </div>
                                    );
                                })}
                            </div>
                            <p className="pc-mot-hint">
                                Как в файле ЗП: меньше 100 ч — 5 р/ч, меньше 135 — 5.5, меньше 165 — 6. Последний порог в режиме «сверх нормы» — без верхней границы (и выше).
                            </p>
                        </div>

                        <div className="pc-emp-sect">
                            <div className="pc-emp-sect__t">
                                Премия по OCC
                                <button type="button" onClick={() => setConfig({ occBonusTiers: [...form.config.occBonusTiers, emptyOccTier()] })}>Добавить диапазон</button>
                            </div>
                            <div className="pc-mot-table">
                                <div className="pc-mot-tr pc-mot-tr--h pc-mot-tr--occ">
                                    <span>OCC от, %</span>
                                    <span>OCC до, %</span>
                                    <span>Премия, %</span>
                                    <span />
                                </div>
                                {(form.config.occBonusTiers || []).map((tier, index) => (
                                    <div className="pc-mot-tr pc-mot-tr--occ" key={`o-${index}`}>
                                        <input className="pc-emp-inp pc-emp-inp--mono" type="number" value={tier.occFrom} onChange={(e) => updateTier('occBonusTiers', index, { occFrom: num(e.target.value) })} />
                                        <input className="pc-emp-inp pc-emp-inp--mono" type="number" value={tier.occTo} onChange={(e) => updateTier('occBonusTiers', index, { occTo: num(e.target.value) })} />
                                        <input className="pc-emp-inp pc-emp-inp--mono" type="number" value={tier.percent} onChange={(e) => updateTier('occBonusTiers', index, { percent: num(e.target.value) })} />
                                        <button type="button" className="pc-emp-btn pc-emp-btn--sm" onClick={() => removeTier('occBonusTiers', index)}>Удалить</button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pc-emp-card">
                            <div className="ch">
                                <h3>Проверка формулы</h3>
                            </div>
                            <div className="cb">
                                <div className="pc-emp-fg pc-emp-fg--3">
                                    <Field label="Общее время, ч">
                                        <input className="pc-emp-inp pc-emp-inp--mono" type="number" value={previewHours} onChange={(e) => setPreviewHours(e.target.value)} />
                                    </Field>
                                    <Field label="OCC, %">
                                        <input className="pc-emp-inp pc-emp-inp--mono" type="number" value={previewOcc} onChange={(e) => setPreviewOcc(e.target.value)} />
                                    </Field>
                                    <Field label="Оформление">
                                        <select className="pc-emp-inp" value={previewKind} onChange={(e) => setPreviewKind(e.target.value)}>
                                            <option value="Contractor">Подряд</option>
                                            <option value="Contract">Контракт</option>
                                        </select>
                                    </Field>
                                    <Field label="Норма полной ставки, ч">
                                        <input className="pc-emp-inp pc-emp-inp--mono" type="number" value={previewNorm} onChange={(e) => setPreviewNorm(e.target.value)} />
                                    </Field>
                                    <Field label="Ставка">
                                        <select className="pc-emp-inp" value={previewParttime} onChange={(e) => setPreviewParttime(e.target.value)}>
                                            <option value="Full">Полная</option>
                                            <option value="Half">Половина</option>
                                            <option value="Quarter">Четверть</option>
                                        </select>
                                    </Field>
                                </div>
                                <div className="pc-mot-preview">
                                    <span>Часы перерасчёт: {formatHours(preview.hoursRecalc)}</span>
                                    <span>База: {formatMoney(preview.basePay)}</span>
                                    <span>Премия {preview.bonusPercent}%: {formatMoney(preview.bonusPay)}</span>
                                    <span>Схема: {formatMoney(preview.schemeTotal)}</span>
                                    <span>Контракт: {formatMoney(preview.contractPay)}</span>
                                    <b>К выплате: {formatMoney(preview.toPay)}</b>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pc-emp-mf">
                    <button type="button" className="pc-emp-btn pc-emp-btn--pri" disabled={saving} onClick={saveNew}>
                        Сохранить как новую версию
                    </button>
                    <button type="button" className="pc-emp-btn" disabled={saving || (selected && (!isDraft || used))} onClick={saveDraft}>
                        Сохранить черновик
                    </button>
                    <button type="button" className="pc-emp-btn" disabled={saving || !selected} onClick={activate}>
                        Сделать активной
                    </button>
                    <button type="button" className="pc-emp-btn" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
};

export default MotivationModal;
